import { IQuoteTemplateConfigSchema, UserRoleEnum } from '@monetize/types/app';
import { SettingVisibilityEnum } from '@monetize/types/experience-config';
import { ALL_ROLES, USER_ROLES_DISPLAY } from '../../../constants/roles';
import { FeatureFlag } from '../../../services/launchDarkly';

/** Admin roles pinned to the top of the permission role list, in this order. */
const PINNED_PERMISSION_ROLES: UserRoleEnum[] = [
  UserRoleEnum.TENANT_ADMIN,
  UserRoleEnum.BILLING_ADMIN,
  UserRoleEnum.SALES_ADMIN,
];

const EXCLUDED_PERMISSION_ROLES: UserRoleEnum[] = [
  UserRoleEnum.APPROVER_ONLY,
  UserRoleEnum.REPORTING,
  UserRoleEnum.SALES_MANAGER,
  UserRoleEnum.SETTINGS_READ_ONLY,
];

type QuoteTemplateFieldName = keyof IQuoteTemplateConfigSchema;

/**
 * Single source of truth for the Quote Template customization settings that
 * support role-based visibility. Drives both the Settings > Quote Templates
 * admin UI and the per-quote enforcement in QuoteTemplateForm, so the two never
 * drift.
 *
 * `key` is the canonical field name stored in the quote-template values config
 * and used as the permission key in experience config. For most settings the
 * per-quote form binds to the same field name; the one exception (Rate
 * Description) is noted via `quoteFieldName`.
 */
export interface QuoteTemplateSettingMeta {
  /** Canonical field name — permission key + tenant-side form field name. */
  key: QuoteTemplateFieldName;
  label: string;
  tooltip?: string;
  section: 'includedSections' | 'offeringDetails';
  /** Field name used in the per-quote (source="quote") form, if it differs. */
  quoteFieldName?: QuoteTemplateFieldName;
  /** Only rendered for amendment (V2) quotes. */
  isAmendmentOnly?: boolean;
  /** Hidden when the tenant has the billing schedule disabled. */
  requiresBillingScheduleEnabled?: boolean;
  /**
   * Hidden while this (boolean) feature flag is off, in the tenant settings
   * list as well as the per-quote form — otherwise an admin could still set a
   * tenant default for a feature nobody is meant to have yet.
   */
  requiresFeatureFlag?: keyof FeatureFlag;
  /** Toggle stores the inverse of what is shown (e.g. Full Latest Contract). */
  inverted?: boolean;
  /** Toggle defaults to on when the stored value is undefined (e.g. Amount). */
  defaultCheckedWhenUndefined?: boolean;
  /**
   * Nested under this (plain boolean) setting: rendered indented, and shown as
   * off and disabled while the parent is off, since it has no effect then. The
   * stored value is left untouched.
   */
  parentKey?: QuoteTemplateFieldName;
}

export const QUOTE_TEMPLATE_SETTINGS_METADATA: QuoteTemplateSettingMeta[] = [
  {
    key: 'hideUpdatedContractWhenDeltaViewIsSuccessfulOnAmendments',
    label: 'Full Latest Contract',
    section: 'includedSections',
    inverted: true,
  },
  {
    key: 'showHeaderTotal',
    label: 'Amount',
    tooltip: 'Show the total amount at the top of the Quote',
    section: 'includedSections',
    defaultCheckedWhenUndefined: true,
  },
  {
    key: 'showYearlyBreakDown',
    label: 'Include Annual Breakdown',
    section: 'includedSections',
    requiresFeatureFlag: 'enableQuoteYearlyBreakdown',
  },
  {
    key: 'showBillingSchedule',
    label: 'Billing Schedule',
    section: 'includedSections',
    requiresBillingScheduleEnabled: true,
  },
  {
    key: 'showBillingFrequency',
    label: 'Billing Frequency',
    section: 'includedSections',
    requiresBillingScheduleEnabled: true,
  },
  {
    key: 'showPricing',
    label: 'Pricing Details',
    section: 'includedSections',
  },
  {
    key: 'showDiscount',
    label: 'Discounting Details',
    section: 'includedSections',
  },
  {
    key: 'showDiscountSection',
    label: 'Discounting Section',
    tooltip:
      'Show the Discounts summary section on the Quote. Discounting Details must be on for this section to appear.',
    section: 'includedSections',
    defaultCheckedWhenUndefined: true,
    parentKey: 'showDiscount',
  },
  {
    key: 'showSigningPage',
    label: 'Signing Page',
    section: 'includedSections',
  },
  {
    key: 'showPriorContractOnAmendments',
    label: 'Prior Contract (Amendments Only)',
    section: 'includedSections',
    isAmendmentOnly: true,
  },
  {
    key: 'showDeltaViewOnAmendments',
    label: 'Differences from Prior Contract (Amendments Only)',
    tooltip:
      'Note: the Full Latest Contract will still show, regardless of this setting, if the Differences From Prior Contract is not available for this Quote',
    section: 'includedSections',
    isAmendmentOnly: true,
  },
  {
    key: 'showLineLevelAmount',
    label: 'Line Amount',
    section: 'offeringDetails',
    defaultCheckedWhenUndefined: true,
  },
  {
    key: 'showRateName',
    label: 'Rate Name',
    section: 'offeringDetails',
  },
  {
    key: 'showRateDescription',
    label: 'Rate Description',
    section: 'offeringDetails',
    quoteFieldName: 'showOfferingDescription',
  },
  {
    key: 'showProductDescription',
    label: 'Product Description',
    section: 'offeringDetails',
  },
  {
    key: 'showPricePerUnit',
    label: 'Price per Unit',
    section: 'offeringDetails',
  },
  {
    key: 'showCredits',
    label: 'Credits',
    tooltip:
      'Show the credits that are created as part of an upgrade, downgrade, removal of an offering, or other actions that create credits',
    section: 'offeringDetails',
  },
  {
    key: 'showProration',
    label: 'Proration',
    tooltip: 'Show the prorated period for each offering, and scheduled change',
    section: 'offeringDetails',
  },
  {
    key: 'showUnusedItems',
    label: 'Unused Quote Items',
    tooltip: 'Display Quote Items with 0 quantity and 0 amount.',
    section: 'offeringDetails',
  },
];

/**
 * Maps a per-quote form field name to its canonical permission key. Handles the
 * Rate Description setting whose field name differs between tenant and quote.
 */
export const QUOTE_TEMPLATE_FIELD_TO_PERMISSION_KEY: Record<string, string> =
  QUOTE_TEMPLATE_SETTINGS_METADATA.reduce<Record<string, string>>(
    (acc, { key, quoteFieldName }) => {
      acc[key] = key;
      if (quoteFieldName) {
        acc[quoteFieldName] = key;
      }
      return acc;
    },
    {},
  );

/** Admin roles first (Tenant/Billing/Sales Admin), then the rest alphabetically by display name. */
export const QUOTE_TEMPLATE_PERMISSION_ROLES: UserRoleEnum[] = [
  ...PINNED_PERMISSION_ROLES,
  ...ALL_ROLES.filter(
    (role) =>
      !PINNED_PERMISSION_ROLES.includes(role) &&
      !EXCLUDED_PERMISSION_ROLES.includes(role),
  ).sort((roleA, roleB) =>
    USER_ROLES_DISPLAY[roleA].localeCompare(USER_ROLES_DISPLAY[roleB]),
  ),
];

export const SETTING_VISIBILITY_DISPLAY: Record<SettingVisibilityEnum, string> =
  {
    [SettingVisibilityEnum.EDITABLE]: 'Editable',
    [SettingVisibilityEnum.READ_ONLY]: 'Read Only',
    [SettingVisibilityEnum.HIDDEN]: 'Hidden',
  };

export const SETTING_ACCESS_SCOPE_LABEL = {
  ALL_ROLES: 'All Roles',
  SPECIFIC_ROLES: 'Specific Roles',
} as const;
